import type { NotificationPageData, Section } from "./domain";
import { createHttpNotificationTransport } from "./notificationTransport";

export type DocumentNotificationIdentity = { module?:Section; documentId:string; version?:number; verificationPath?:string };
export const DOCUMENT_OPENED_EVENT = "pharmhub:document-opened";

const documentResources = [
  "/api/v1/procurement/physical-receivings/", "/api/v1/execution-requests/",
  "/api/v1/inventory/opening-stock-receipts/", "/api/v1/finance/supplier-payment-requests/",
  "/api/v1/retail/daily-cash-reports/", "/api/v1/finance/supplier-debt-adjustments/",
  ...["purchase_order","customer_order","transfer","stocktake","supplier_return","payment","cash_handover","company_cash_movement","pharmacy_cash_expense"]
    .map(kind=>`/api/v1/notifications/document-versions/${kind}/`),
];
function verifiedResource(identity:DocumentNotificationIdentity):string|null {
  if(!/^[A-Za-z0-9_-]{1,160}$/.test(identity.documentId)||!Number.isSafeInteger(identity.version)||Number(identity.version)<1)return null;
  return documentResources.some(prefix=>identity.verificationPath===`${prefix}${identity.documentId}`)?identity.verificationPath!:null;
}

/** Read only captured recipient notices after an authorized, uncached business-version check. */
export async function acknowledgeDocumentNotifications(identity:DocumentNotificationIdentity, signal?:AbortSignal) {
  const verificationPath=verifiedResource(identity);
  if(!verificationPath||signal?.aborted)return;
  const params=new URLSearchParams({documentId:identity.documentId,status:"unread",limit:"200",offset:"0"});
  if(identity.module)params.set("module",identity.module);
  const captured:{id:string;version:number}[]=[];
  let offset=0,complete=false;
  // Collect before any read mutation: offset must never be advanced through a shrinking unread set.
  for(let batch=0;batch<10&&!signal?.aborted;batch++){
    params.set("offset",String(offset));
    const response=await fetch(`/api/v1/notifications?${params}`,{credentials:"include",cache:"no-store",signal});
    if(!response.ok)throw new Error("document notifications unavailable");
    const page=await response.json() as NotificationPageData;
    if(!Array.isArray(page.items)||!Number.isSafeInteger(page.total)||page.total<0)return;
    captured.push(...page.items.filter(item=>typeof item.id==="string"&&item.documentId===identity.documentId&&(!identity.module||item.module===identity.module)&&!item.readAt&&Number.isSafeInteger(item.version)&&item.version>=1).map(item=>({id:item.id,version:item.version})));
    offset+=page.items.length;
    if(offset>=page.total){complete=true;break;}
    if(!page.items.length)return;
  }
  if(!complete)return;
  if(!captured.length||signal?.aborted)return;
  const currentResponse=await fetch(verificationPath,{credentials:"include",cache:"no-store",signal});
  if(!currentResponse.ok||signal?.aborted)return;
  const current=await currentResponse.json() as {id?:unknown;version?:unknown};
  if(!current||current.id!==identity.documentId||!Number.isSafeInteger(current.version)||current.version!==identity.version)return;
  const transport=createHttpNotificationTransport();
  const marked=new Set<string>();
  for(const item of captured){
    if(signal?.aborted)return;
    if(!marked.has(item.id)){await transport.markRead(item.id,item.version);marked.add(item.id);}
  }
}
